import { MetricData } from '@chiroup/core/types/AdminMetrics.type';
import { motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';
import { ACQUISITION_METRICS, SUBSCRIBER_METRICS } from '../../../constants/metrics.constants';
import MetricInfoTooltip from './MetricInfoTooltip';

interface AcquisitionFunnelProps {
  metrics: MetricData[];
}

const AcquisitionFunnel: React.FC<AcquisitionFunnelProps> = ({ metrics }) => {
  // State for tooltip visibility - avoid direct DOM manipulation
  const [visibleTooltips, setVisibleTooltips] = useState<Set<string>>(new Set());

  // Memoize metric extraction and processing - only recalculates when metrics change
  const funnelData = useMemo(() => {
    const getMetricValue = (id: string): number => {
      const metric = metrics.find(m => m.id === id);

      return typeof metric?.value === 'number' ? metric.value : 0;
    };

    // Combine total leads with campaign members
    const totalLeadsBase = getMetricValue(ACQUISITION_METRICS.TOTAL_LEADS);
    const campaignMembers = getMetricValue(ACQUISITION_METRICS.CAMPAIGN_MEMBERS_NEW_LEADS);
    const combinedLeads = totalLeadsBase + campaignMembers;

    return {
      leads: combinedLeads,
      totalEstimatedLeads: getMetricValue(ACQUISITION_METRICS.TOTAL_ESTIMATED_LEADS),
      trialsConversion: getMetricValue(ACQUISITION_METRICS.LEADS_TO_TRIALS_CONVERSION),
      demosConversion: getMetricValue(ACQUISITION_METRICS.LEADS_TO_DEMOS_CONVERSION),
      trials: getMetricValue(ACQUISITION_METRICS.ESSENTIALS_TRIALS),
      demos: getMetricValue(ACQUISITION_METRICS.EHR_DEMOS),
      essentialsCloseRate: getMetricValue(ACQUISITION_METRICS.ESSENTIALS_CLOSE_RATE),
      ehrCloseRate: getMetricValue(ACQUISITION_METRICS.EHR_CLOSE_RATE),
      essentialsSubscribers: getMetricValue(SUBSCRIBER_METRICS.ESSENTIALS_TOTAL),
      ehrSubscribers: getMetricValue(SUBSCRIBER_METRICS.EHR_TOTAL),
      organicSubscribers: getMetricValue(SUBSCRIBER_METRICS.ESSENTIALS_ORGANIC),
      seminarSubscribers: getMetricValue(SUBSCRIBER_METRICS.ESSENTIALS_SEMINAR),
    };
  }, [metrics]);

  // Full metric lookup for info tooltips
  const getMetric = (id: string): MetricData | undefined => metrics.find(m => m.id === id);

  // Optimized tooltip handlers using React state instead of DOM manipulation
  const handleTooltipShow = useCallback((tooltipId: string) => {
    setVisibleTooltips(prev => new Set(prev).add(tooltipId));
  }, []);

  const handleTooltipHide = useCallback((tooltipId: string) => {
    setVisibleTooltips(prev => {
      const newSet = new Set(prev);
      newSet.delete(tooltipId);

      return newSet;
    });
  }, []);

  return (
    <div className="max-w-5xl mx-auto py-6">
      {/* Enhanced Header */}
      <motion.div
        className="text-center mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <motion.h2
          className="text-3xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-3"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          Acquisition Funnel
        </motion.h2>
      </motion.div>

      {/* Leads - Top Level */}
      <div className="flex justify-center mb-8">
        <motion.div
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl px-12 py-8 shadow-xl text-center w-full max-w-3xl relative overflow-hidden
                     hover:shadow-2xl hover:scale-[1.02] transition-all duration-500 ease-out cursor-pointer group"
          whileHover={{
            boxShadow: '0 25px 50px -12px rgba(59, 130, 246, 0.25)',
            transition: { duration: 0.3 },
          }}
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent group-hover:from-white/10 transition-all duration-500"></div>

          {/* Total Estimated Leads - Primary Metric */}
          <div className="relative z-10 mb-6">
            <motion.div
              className="flex items-center justify-center gap-2 mb-2"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <motion.svg
                className="w-5 h-5 text-white/80"
                fill="currentColor"
                viewBox="0 0 20 20"
                whileHover={{ rotate: [0, -5, 5, 0] }}
                transition={{ duration: 0.3 }}
              >
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
              </motion.svg>
              <div className="text-xs opacity-70 uppercase tracking-wider font-medium">
                Since 2012
              </div>
            </motion.div>
            <div className="text-5xl font-black mb-2 drop-shadow-sm">
              {funnelData.totalEstimatedLeads.toLocaleString()}
            </div>
            <div className="inline-flex items-center gap-1 text-sm opacity-90 uppercase tracking-wide font-semibold">
              TOTAL ESTIMATED LEADS
              <MetricInfoTooltip
                info={getMetric(ACQUISITION_METRICS.TOTAL_ESTIMATED_LEADS)?.info}
                query={getMetric(ACQUISITION_METRICS.TOTAL_ESTIMATED_LEADS)?.query}
                className="text-white/70 hover:text-white"
              />
            </div>
          </div>

          {/* New Leads - Secondary Metric with Context */}
          <motion.div
            className="relative z-10 bg-white/10 rounded-xl py-4 backdrop-blur-sm border border-white/20 mx-auto w-80                       hover:bg-white/15 hover:border-white/30 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-center gap-6 px-4">
              <motion.div
                className="text-center"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div className="text-2xl font-bold">{funnelData.leads.toLocaleString()}</div>
                <div className="text-xs opacity-80 uppercase tracking-wide">NEW LEADS</div>
              </motion.div>
              {funnelData.totalEstimatedLeads > 0 && (
                <div className="text-center">
                  <motion.div
                    className="bg-gradient-to-r from-blue-200 to-blue-300 text-blue-800 px-3 py-1 rounded-full text-sm font-bold shadow-lg
                               hover:from-blue-300 hover:to-blue-400 hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.1, rotate: [0, -2, 2, 0] }}
                    transition={{ duration: 0.2 }}
                  >
                    {((funnelData.leads / funnelData.totalEstimatedLeads) * 100).toFixed(1)}%
                  </motion.div>
                  <div className="text-xs opacity-70 mt-1">of total</div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Arrows Down - aligned with other arrows in funnel */}
      <div className="flex justify-center my-3 w-full max-w-3xl mx-auto">
        <div className="flex justify-center flex-1">
          <motion.div
            className="text-green-500 text-3xl text-center cursor-pointer"
            whileHover={{ scale: 1.2, y: 5 }}
            animate={{ y: [0, 5, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 2, ease: 'easeInOut' },
              scale: { duration: 0.2 },
            }}
          >
            ▼
          </motion.div>
        </div>
        <div className="flex-1"></div>
        <div className="flex justify-center flex-1">
          <motion.div
            className="text-purple-500 text-3xl text-center cursor-pointer"
            whileHover={{ scale: 1.2, y: 5 }}
            animate={{ y: [0, 5, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 2, ease: 'easeInOut', delay: 0.5 },
              scale: { duration: 0.2 },
            }}
          >
            ▼
          </motion.div>
        </div>
      </div>

      {/* Pathway Labels - perfectly centered between arrows and conversion rates */}
      <div className="flex justify-center mb-5 w-full max-w-3xl mx-auto">
        <div className="flex justify-center flex-1">
          <motion.div
            className="bg-green-100 border-2 border-green-300 rounded-lg px-4 py-2 text-center w-44 shadow-sm
                       hover:shadow-lg hover:scale-105 hover:bg-green-200 hover:border-green-400 
                       transition-all duration-300 cursor-pointer group"
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div
              className="text-green-800 font-semibold text-base group-hover:text-green-900"
              whileHover={{ scale: 1.05 }}
            >
              ESSENTIALS
            </motion.div>
            <div className="text-green-600 text-sm group-hover:text-green-700">Trial Pathway</div>
          </motion.div>
        </div>
        <div className="flex-1"></div>
        <div className="flex justify-center flex-1">
          <motion.div
            className="bg-purple-100 border-2 border-purple-300 rounded-lg px-4 py-2 text-center w-44 shadow-sm
                       hover:shadow-lg hover:scale-105 hover:bg-purple-200 hover:border-purple-400 
                       transition-all duration-300 cursor-pointer group"
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div
              className="text-purple-800 font-semibold text-base group-hover:text-purple-900"
              whileHover={{ scale: 1.05 }}
            >
              EHR
            </motion.div>
            <div className="text-purple-600 text-sm group-hover:text-purple-700">Demo Pathway</div>
          </motion.div>
        </div>
      </div>

      {/* Conversion Rates */}
      <div className="flex justify-center mt-5 mb-5 w-full max-w-3xl mx-auto">
        <div className="flex justify-center flex-1">
          <motion.div
            className="bg-blue-50 border-2 border-blue-200 rounded-full px-4 py-2 text-center shadow-sm group relative
                       hover:shadow-lg hover:scale-110 hover:bg-blue-100 hover:border-blue-300 
                       transition-all duration-300 cursor-pointer"
            data-tooltip-id="trialsConversion"
            onMouseEnter={() => handleTooltipShow('trialsConversion')}
            onMouseLeave={() => handleTooltipHide('trialsConversion')}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              className="text-blue-700 font-bold text-base group-hover:text-blue-800"
              whileHover={{ scale: 1.1 }}
            >
              {funnelData.trialsConversion.toFixed(1)}%
            </motion.div>
            <motion.div
              className={`tooltip absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs bg-gradient-to-br from-white/95 to-gray-50/95 dark:from-gray-800/95 dark:to-gray-900/95 text-gray-800 dark:text-white rounded-xl shadow-xl border border-white/50 dark:border-gray-700/50 backdrop-blur-md z-[9999] ring-1 ring-black/5 dark:ring-white/10 min-w-[220px] ${
                visibleTooltips.has('trialsConversion') ? 'visible' : 'invisible'
              }`}
              style={{
                boxShadow:
                  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              }}
              animate={{
                opacity: visibleTooltips.has('trialsConversion') ? 1 : 0,
                scale: visibleTooltips.has('trialsConversion') ? 1 : 0.9,
              }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                  <div className="text-xs font-semibold">Leads to Trials</div>
                </div>
                <div className="text-sm font-bold mb-1">
                  {funnelData.trialsConversion.toFixed(1)}%
                </div>
                <div className="text-xs opacity-70 font-medium">
                  {funnelData.trials} trials from {funnelData.leads.toLocaleString()} leads
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
        <div className="flex-1"></div>
        <div className="flex justify-center flex-1">
          <motion.div
            className="bg-blue-50 border-2 border-blue-200 rounded-full px-4 py-2 text-center shadow-sm group relative
                       hover:shadow-lg hover:scale-110 hover:bg-blue-100 hover:border-blue-300 
                       transition-all duration-300 cursor-pointer"
            data-tooltip-id="demosConversion"
            onMouseEnter={() => handleTooltipShow('demosConversion')}
            onMouseLeave={() => handleTooltipHide('demosConversion')}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              className="text-blue-700 font-bold text-base group-hover:text-blue-800"
              whileHover={{ scale: 1.1 }}
            >
              {funnelData.demosConversion.toFixed(1)}%
            </motion.div>
            <motion.div
              className={`tooltip absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs bg-gradient-to-br from-white/95 to-gray-50/95 dark:from-gray-800/95 dark:to-gray-900/95 text-gray-800 dark:text-white rounded-xl shadow-xl border border-white/50 dark:border-gray-700/50 backdrop-blur-md z-[9999] ring-1 ring-black/5 dark:ring-white/10 min-w-[220px] ${
                visibleTooltips.has('demosConversion') ? 'visible' : 'invisible'
              }`}
              style={{
                boxShadow:
                  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              }}
              animate={{
                opacity: visibleTooltips.has('demosConversion') ? 1 : 0,
                scale: visibleTooltips.has('demosConversion') ? 1 : 0.9,
              }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  <div className="text-xs font-semibold">Leads to Demos</div>
                </div>
                <div className="text-sm font-bold mb-1">
                  {funnelData.demosConversion.toFixed(1)}%
                </div>
                <div className="text-xs opacity-70 font-medium">
                  {funnelData.demos} demos from {funnelData.leads.toLocaleString()} leads
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Trials & Demos - Middle Level */}
      <div className="flex justify-center mb-5 w-full max-w-3xl mx-auto">
        <motion.div
          className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl px-8 py-5 shadow-xl flex-1 text-center
                     hover:shadow-2xl hover:scale-105 hover:from-green-600 hover:to-green-700 
                     transition-all duration-500 cursor-pointer group relative overflow-hidden"
          whileHover={{ y: -5 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="inline-flex items-center gap-1 text-sm font-medium opacity-90 uppercase tracking-wide mb-2 relative z-10">
            ESSENTIALS TRIALS
            <MetricInfoTooltip
              info={getMetric(ACQUISITION_METRICS.ESSENTIALS_TRIALS)?.info}
              query={getMetric(ACQUISITION_METRICS.ESSENTIALS_TRIALS)?.query}
              className="text-white/70 hover:text-white"
            />
          </div>
          <motion.div className="text-3xl font-bold relative z-10" whileHover={{ scale: 1.1 }}>
            {funnelData.trials.toLocaleString()}
          </motion.div>
        </motion.div>
        <div className="flex-1"></div>
        <motion.div
          className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl px-8 py-5 shadow-xl flex-1 text-center
                     hover:shadow-2xl hover:scale-105 hover:from-green-600 hover:to-green-700 
                     transition-all duration-500 cursor-pointer group relative overflow-hidden"
          whileHover={{ y: -5 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="inline-flex items-center gap-1 text-sm font-medium opacity-90 uppercase tracking-wide mb-2 relative z-10">
            EHR DEMOS
            <MetricInfoTooltip
              info={getMetric(ACQUISITION_METRICS.EHR_DEMOS)?.info}
              query={getMetric(ACQUISITION_METRICS.EHR_DEMOS)?.query}
              className="text-white/70 hover:text-white"
            />
          </div>
          <motion.div className="text-3xl font-bold relative z-10" whileHover={{ scale: 1.1 }}>
            {funnelData.demos.toLocaleString()}
          </motion.div>
        </motion.div>
      </div>

      {/* Arrows Down */}
      <div className="flex justify-center my-3 w-full max-w-3xl mx-auto">
        <div className="flex justify-center flex-1">
          <motion.div
            className="text-blue-500 text-3xl text-center cursor-pointer"
            whileHover={{ scale: 1.2, y: 5 }}
            animate={{ y: [0, 5, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 2, ease: 'easeInOut' },
              scale: { duration: 0.2 },
            }}
          >
            ▼
          </motion.div>
        </div>
        <div className="flex-1"></div>
        <div className="flex justify-center flex-1">
          <motion.div
            className="text-blue-500 text-3xl text-center cursor-pointer"
            whileHover={{ scale: 1.2, y: 5 }}
            animate={{ y: [0, 5, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 2, ease: 'easeInOut', delay: 0.5 },
              scale: { duration: 0.2 },
            }}
          >
            ▼
          </motion.div>
        </div>
      </div>

      {/* Close Rates */}
      <div className="flex justify-center mt-5 mb-5 w-full max-w-3xl mx-auto">
        <div className="flex justify-center flex-1">
          <motion.div
            className="bg-blue-50 border-2 border-blue-200 rounded-full px-4 py-2 text-center shadow-sm group relative
                       hover:shadow-lg hover:scale-110 hover:bg-blue-100 hover:border-blue-300 
                       transition-all duration-300 cursor-pointer"
            data-tooltip-id="essentialsCloseRate"
            onMouseEnter={() => handleTooltipShow('essentialsCloseRate')}
            onMouseLeave={() => handleTooltipHide('essentialsCloseRate')}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              className="text-blue-700 font-bold text-base group-hover:text-blue-800"
              whileHover={{ scale: 1.1 }}
            >
              {funnelData.essentialsCloseRate.toFixed(1)}%
            </motion.div>
            <motion.div
              className={`tooltip absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs bg-gradient-to-br from-white/95 to-gray-50/95 dark:from-gray-800/95 dark:to-gray-900/95 text-gray-800 dark:text-white rounded-xl shadow-xl border border-white/50 dark:border-gray-700/50 backdrop-blur-md z-[9999] ring-1 ring-black/5 dark:ring-white/10 min-w-[220px] ${
                visibleTooltips.has('essentialsCloseRate') ? 'visible' : 'invisible'
              }`}
              style={{
                boxShadow:
                  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              }}
              animate={{
                opacity: visibleTooltips.has('essentialsCloseRate') ? 1 : 0,
                scale: visibleTooltips.has('essentialsCloseRate') ? 1 : 0.9,
              }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                  <div className="text-xs font-semibold">Essentials Close Rate</div>
                </div>
                <div className="text-sm font-bold mb-1">
                  {funnelData.essentialsCloseRate.toFixed(1)}%
                </div>
                <div className="text-xs opacity-70 font-medium">
                  {funnelData.essentialsSubscribers} subscribers from {funnelData.trials} trials
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
        <div className="flex-1"></div>
        <div className="flex justify-center flex-1">
          <motion.div
            className="bg-blue-50 border-2 border-blue-200 rounded-full px-4 py-2 text-center shadow-sm group relative
                       hover:shadow-lg hover:scale-110 hover:bg-blue-100 hover:border-blue-300 
                       transition-all duration-300 cursor-pointer"
            data-tooltip-id="ehrCloseRate"
            onMouseEnter={() => handleTooltipShow('ehrCloseRate')}
            onMouseLeave={() => handleTooltipHide('ehrCloseRate')}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              className="text-blue-700 font-bold text-base group-hover:text-blue-800"
              whileHover={{ scale: 1.1 }}
            >
              {funnelData.ehrCloseRate.toFixed(1)}%
            </motion.div>
            <motion.div
              className={`tooltip absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 text-xs bg-gradient-to-br from-white/95 to-gray-50/95 dark:from-gray-800/95 dark:to-gray-900/95 text-gray-800 dark:text-white rounded-xl shadow-xl border border-white/50 dark:border-gray-700/50 backdrop-blur-md z-[9999] ring-1 ring-black/5 dark:ring-white/10 min-w-[220px] ${
                visibleTooltips.has('ehrCloseRate') ? 'visible' : 'invisible'
              }`}
              style={{
                boxShadow:
                  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              }}
              animate={{
                opacity: visibleTooltips.has('ehrCloseRate') ? 1 : 0,
                scale: visibleTooltips.has('ehrCloseRate') ? 1 : 0.9,
              }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  <div className="text-xs font-semibold">EHR Close Rate</div>
                </div>
                <div className="text-sm font-bold mb-1">{funnelData.ehrCloseRate.toFixed(1)}%</div>
                <div className="text-xs opacity-70 font-medium">
                  {funnelData.ehrSubscribers} subscribers from {funnelData.demos} demos
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Subscribers - Bottom Level */}
      <div className="flex justify-center mt-5 w-full max-w-3xl mx-auto">
        <motion.div
          className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl px-8 py-5 shadow-xl flex-1 text-center
                     hover:shadow-2xl hover:scale-105 hover:from-orange-600 hover:to-orange-700 
                     transition-all duration-500 cursor-pointer group relative overflow-hidden"
          whileHover={{ y: -8 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="inline-flex items-center gap-1 text-sm font-medium opacity-90 uppercase tracking-wide mb-2 relative z-10">
            ESSENTIALS SUBSCRIBERS
            <MetricInfoTooltip
              info={getMetric(SUBSCRIBER_METRICS.ESSENTIALS_TOTAL)?.info}
              query={getMetric(SUBSCRIBER_METRICS.ESSENTIALS_TOTAL)?.query}
              className="text-white/70 hover:text-white"
            />
          </div>
          <motion.div className="text-3xl font-bold mb-3 relative z-10" whileHover={{ scale: 1.1 }}>
            {funnelData.essentialsSubscribers.toLocaleString()}
          </motion.div>
          <div className="border-t border-orange-300/50 pt-3 space-y-1 relative z-10">
            <motion.div
              className="flex justify-between text-xs opacity-90"
              whileHover={{ scale: 1.02, x: 2 }}
            >
              <span className="inline-flex items-center gap-1">
                Organic:
                <MetricInfoTooltip
                  info={getMetric(SUBSCRIBER_METRICS.ESSENTIALS_ORGANIC)?.info}
                  query={getMetric(SUBSCRIBER_METRICS.ESSENTIALS_ORGANIC)?.query}
                  className="text-white/70 hover:text-white"
                />
              </span>
              <span className="font-semibold">
                {funnelData.organicSubscribers.toLocaleString()}
              </span>
            </motion.div>
            <motion.div
              className="flex justify-between text-xs opacity-90"
              whileHover={{ scale: 1.02, x: 2 }}
            >
              <span className="inline-flex items-center gap-1">
                Seminar:
                <MetricInfoTooltip
                  info={getMetric(SUBSCRIBER_METRICS.ESSENTIALS_SEMINAR)?.info}
                  query={getMetric(SUBSCRIBER_METRICS.ESSENTIALS_SEMINAR)?.query}
                  className="text-white/70 hover:text-white"
                />
              </span>
              <span className="font-semibold">
                {funnelData.seminarSubscribers.toLocaleString()}
              </span>
            </motion.div>
          </div>
        </motion.div>
        <div className="flex-1"></div>
        <motion.div
          className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl px-8 py-5 shadow-xl flex-1 text-center
                     hover:shadow-2xl hover:scale-105 hover:from-orange-600 hover:to-orange-700 
                     transition-all duration-500 cursor-pointer group relative overflow-hidden"
          whileHover={{ y: -8 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="inline-flex items-center gap-1 text-sm font-medium opacity-90 uppercase tracking-wide mb-2 relative z-10">
            EHR SUBSCRIBERS
            <MetricInfoTooltip
              info={getMetric(SUBSCRIBER_METRICS.EHR_TOTAL)?.info}
              query={getMetric(SUBSCRIBER_METRICS.EHR_TOTAL)?.query}
              className="text-white/70 hover:text-white"
            />
          </div>
          <motion.div className="text-3xl font-bold relative z-10" whileHover={{ scale: 1.1 }}>
            {funnelData.ehrSubscribers.toLocaleString()}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default AcquisitionFunnel;
